import React from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Box,
  CardHeader,
  CardContent,
  Card,
  Divider,
} from "@mui/material";
import { DbUser } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import { makeStyles } from "@mui/styles";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Role, SubRoles } from "../../../../enums";
import { generateGtmEvent } from "../../../../Helpers";
import { ViewUserManagement } from "@backend-types";
import { useEffect, useState } from "react";
import axios from "../../../../services/axios";
import { logger } from "../../../../services/logger";

const useStyles = makeStyles((theme) => ({
  pane: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
  },
  paneHeader: {
    backgroundColor: "#FAFAFA",
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: "16px 24px 16px 24px",
  },
  paneTitle: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    lineHeight: "1em",
    letterSpacing: "0em",
  },
  paneBody: { padding: "24px" },
  textField: {
    "& .MuiFormLabel-root": {
      fontWeight: 600,
      fontSize: "16px",
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.secondary,
      transform: "unset",
    },
    "& .MuiInputBase-root": {
      color: "#484848",
      paddingTop: "8px",
      paddingBottom: "0px",
      borderBottom: "none !important",
      fontSize: 16,
      fontWeight: 400,
      "& .Mui-disabled": {
        "-webkit-text-fill-color": "unset",
      },
    },
  },
  borderBottom: {
    width: "100%",
    borderBottom: "1px solid #E8E8E8",
  },
}));

export type ViewUserProps = {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  currentUser: DbUser;
  user: ViewUserManagement;
};

export function ViewUser(props: ViewUserProps) {
  const classes = useStyles();
  const [selectedUser, setSelectedUser] = useState<DbUser | null>(null);

  const fullName = `${props.user.first_name} ${props.user.last_name}`;
  let displayEmail =
    props.currentUser?.role !== Role.teacher ||
    (props.currentUser?.role === Role.teacher &&
      props.user.teacher_id[0] === props.currentUser.teacher_id);
  const displaySubRole = props.currentUser?.role === Role.super;

  const fieldProps = {
    fullWidth: true,
    disabled: true,
    className: classes.textField,
    InputProps: {
      disableUnderline: true,
    },
  };

  const mode: string = "v2";

  const divider = <div className={classes.borderBottom} />;
  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string
  ) => {
    generateGtmEvent({ event, action, name, type, region, section, text });
  };

  useEffect(() => {
    const getSelectedUser = async (userId: string) => {
      try {
        const result = await axios.get(`/user/${userId}`);
        if (result.data && result.data.id) {
          const u = result.data as DbUser;
          setSelectedUser(u);
        }
      } catch (e) {
        logger.error(e);
        throw e;
      }
    };
    if (props.user) {
      if (props.open) getSelectedUser(props.user.id);
    } else {
      if (props.open) {
        setSelectedUser(null);
      }
    }
  }, [props.user, props.open]);

  const selectedUserSchool =
    selectedUser?.user_school && selectedUser?.user_school.length > 0
      ? selectedUser?.user_school[0].school
      : undefined;

  const isReadOnlyAdmin = props.currentUser?.user_subrole?.some(
    (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
  );
  return (
    <UserManagementModal
      hideSubmitButton={true}
      title={fullName}
      titleElem={
        <>
          {fullName}
          {!isReadOnlyAdmin && (
            <Button
              variant="outlined"
              size={"small"}
              disableElevation
              sx={{
                marginLeft: "1em",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
              onClick={() => {
                HandleDataLayer(
                  "link",
                  "click",
                  "onclick",
                  "internal link",
                  "main content",
                  fullName,
                  "edit"
                );
                props.onEdit();
              }}>
              Edit
            </Button>
          )}
        </>
      }
      open={props.open}
      onClose={props.onClose}>
      {mode === "v2" && (
        <>
          <Card className={classes.pane}>
            <CardHeader
              className={classes.paneHeader}
              title={
                <Typography variant="h6" component="h6" className={classes.paneTitle}>
                  Profile
                </Typography>
              }
            />
            <CardContent className={classes.paneBody}>
              <Grid container spacing={5}>
                <Grid item xs={4} order={1}>
                  <TextField
                    label="Name"
                    value={fullName}
                    variant="standard"
                    {...fieldProps}
                  />
                </Grid>
                <Grid item xs={4} order={2}>
                  <TextField
                    label="School"
                    value={selectedUserSchool?.name ?? " "}
                    variant="standard"
                    {...fieldProps}
                  />
                </Grid>
                {displayEmail && (
                  <Grid item xs={4} order={3}>
                    <TextField
                      label="Email"
                      value={selectedUser?.email}
                      variant="standard"
                      {...fieldProps}
                    />
                  </Grid>
                )}
                {/* <Grid item xs={4} order={4}>
                <TextField
                  label="Teacher"
                  value={props.user.teacher_name[0] ?? " "}
                  variant="standard"
                  {...fieldProps}
                />
              </Grid> */}
                <Grid item xs={4} order={displayEmail ? 5 : 3}>
                  <TextField
                    label="Role"
                    value={selectedUser?.role}
                    variant="standard"
                    {...fieldProps}
                  />
                </Grid>
                {/* <Grid item xs={4} order={6}>
                <TextField
                  label="Assigned Class"
                  value={
                    props.user.subject
                      ? `${props.user.subject} ${props.user.period_block} ${
                          props.user.class_start_date[0]
                            ? new Date(props.user.class_start_date[0]).toLocaleDateString(
                                "en-US",
                                {
                                  timeZone: "UTC",
                                }
                              )
                            : ""
                        }`
                      : " "
                  }
                  variant="standard"
                  {...fieldProps}
                />
              </Grid> */}

                <Grid item xs={4} order={displayEmail ? 7 : 5}>
                  <TextField
                    label="Last Accessed Digit"
                    value={
                      selectedUser?.last_access_at
                        ? new Date(selectedUser?.last_access_at).toLocaleDateString(
                            "en-US",
                            {
                              timeZone: "UTC",
                              month: "long",
                              day: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )
                        : "Never"
                    }
                    variant="standard"
                    {...fieldProps}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={4} order={8}>
                  <TextField
                    label="Grade"
                    value={selectedUser?.grade ? selectedUser?.grade : " "}
                    variant="standard"
                    {...fieldProps}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={4} order={displayEmail ? 9 : 7}>
                  <Typography
                    sx={{
                      color: "#747474",
                      fontSize: 10.5,
                      fontWeight: 500,
                      letterSpacing: 0.7,
                    }}>
                    ACCESSIBILITY
                  </Typography>
                  <Box sx={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                    {selectedUser?.accessibility ? <CheckIcon /> : <CloseIcon />}
                    <Typography>
                      {selectedUser?.accessibility ? "Enabled" : "Disabled"}
                    </Typography>
                  </Box>
                </Grid>
                {displaySubRole && (
                  <Grid item xs={4} order={displayEmail ? 11 : 9}>
                    <Typography
                      sx={{
                        color: "#747474",
                        fontSize: 10.5,
                        fontWeight: 500,
                        letterSpacing: 0.7,
                      }}>
                      USER INTERFACE TESTING
                    </Typography>
                    <Box sx={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                      {selectedUser?.is_tester || selectedUser?.is_tester_class ? (
                        <CheckIcon />
                      ) : (
                        <CloseIcon />
                      )}
                      <Typography>
                        {selectedUser?.is_tester
                          ? "Marked as a test user"
                          : selectedUser?.is_tester_class === true
                          ? "Marked as test user through class"
                          : "Not marked as a test user"}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
          {/* Class Associations */}
          {selectedUser?.role === Role.student && (
            <Card className={classes.pane} sx={{ marginTop: "16px" }}>
              <CardHeader
                className={classes.paneHeader}
                title={
                  <Typography variant="h6" component="h6" className={classes.paneTitle}>
                    Classes
                  </Typography>
                }
              />
              <CardContent className={classes.paneBody}>
                {selectedUser?.class_student.map((item, index) => {
                  return (
                    <React.Fragment key={`${item.id}-${index}`}>
                      <Grid container spacing={4} key={`${item.id}-${index}`}>
                        <Grid item xs={6} order={1}>
                          <TextField
                            label="Teacher"
                            value={item.class?.user?.full_name}
                            variant="standard"
                            {...fieldProps}
                          />
                        </Grid>
                        <Grid item xs={6} order={1}>
                          <TextField
                            label="Class"
                            value={`${item.class?.subject} ${item.class?.period_block}`}
                            variant="standard"
                            {...fieldProps}
                          />
                        </Grid>
                      </Grid>
                      {index < selectedUser?.class_student.length - 1 && (
                        <Divider
                          sx={{
                            borderBottomWidth: 1,
                            marginTop: "32px",
                            marginBottom: "32px",
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}
      {mode === "v1" && (
        <Grid container spacing={6}>
          <Grid item xs={6} order={1}>
            <TextField label="Name" value={fullName} variant="standard" {...fieldProps} />
            {divider}
          </Grid>
          <Grid item xs={6} order={2}>
            <TextField
              label="School"
              value={selectedUserSchool?.name ?? " "}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          {displayEmail && (
            <Grid item xs={6} order={3}>
              <TextField
                label="Email"
                value={selectedUser?.email}
                variant="standard"
                {...fieldProps}
              />
              {divider}
            </Grid>
          )}
          <Grid item xs={6} order={4}>
            <TextField
              label="Teacher"
              value={props.user.teacher_name[0] ?? " "}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={displayEmail ? 5 : 3}>
            <TextField
              label="Role"
              value={props.user.role}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={6}>
            <TextField
              label="Assigned Class"
              value={
                props.user.subject
                  ? `${props.user.subject} ${props.user.period_block} ${
                      props.user.class_start_date[0]
                        ? new Date(props.user.class_start_date[0]).toLocaleDateString(
                            "en-US",
                            {
                              timeZone: "UTC",
                            }
                          )
                        : ""
                    }`
                  : " "
              }
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>

          <Grid item xs={6} order={displayEmail ? 7 : 5}>
            <TextField
              label="Last Accessed Digit"
              value={
                selectedUser?.last_access_at
                  ? new Date(selectedUser?.last_access_at).toLocaleDateString("en-US", {
                      timeZone: "UTC",
                      month: "long",
                      day: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : "Never"
              }
              variant="standard"
              {...fieldProps}
              className={classes.textField}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={8}>
            <TextField
              label="Grade"
              value={selectedUser?.grade ? selectedUser?.grade : " "}
              variant="standard"
              {...fieldProps}
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={6} order={displayEmail ? 9 : 7}>
            <Typography
              sx={{
                color: "#747474",
                fontSize: 10.5,
                fontWeight: 500,
                letterSpacing: 0.7,
              }}>
              ACCESSIBILITY
            </Typography>
            <Box sx={{ display: "flex", gap: "16px", marginTop: "8px" }}>
              {selectedUser?.accessibility ? <CheckIcon /> : <CloseIcon />}
              <Typography>
                {selectedUser?.accessibility ? "Enabled" : "Disabled"}
              </Typography>
            </Box>
          </Grid>
          {displaySubRole && (
            <Grid item xs={6} order={displayEmail ? 11 : 9}>
              <Typography
                sx={{
                  color: "#747474",
                  fontSize: 10.5,
                  fontWeight: 500,
                  letterSpacing: 0.7,
                }}>
                USER INTERFACE TESTING
              </Typography>
              <Box sx={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                {selectedUser?.is_tester || selectedUser?.is_tester_class ? (
                  <CheckIcon />
                ) : (
                  <CloseIcon />
                )}
                <Typography>
                  {selectedUser?.is_tester
                    ? "Marked as a test user"
                    : selectedUser?.is_tester_class
                    ? "Marked as test user through class"
                    : "Not marked as a test user"}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </UserManagementModal>
  );
}
